/**
 * useEconomyV2 — Centralized data provider for Economy V2 widgets.
 *
 * Features:
 *   - Parallel fetching (max 3 concurrent)
 *   - Response caching (30s TTL)
 *   - Timeout handling (2.5s per request)
 *   - Silent retry (1 retry on failure)
 *   - Request deduplication
 *   - Slow network detection
 *   - Non-blocking logging
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../utils/api';

const CACHE_TTL = 30_000;   // 30s
const FETCH_TIMEOUT = 5000; // 5s (increased from 2.5s for preview environment)
const MAX_CONCURRENT = 3;

// ── Module-level cache (shared across all hook instances) ──
const _cache = new Map();
const _inflight = new Map(); // dedup: key → Promise

function _cacheGet(key) {
  const entry = _cache.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.ts > CACHE_TTL) { _cache.delete(key); return undefined; }
  return entry.data;
}
function _cacheSet(key, data) {
  _cache.set(key, { data, ts: Date.now() });
}

// ── Timeout wrapper ──
function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// ── Fetch with retry + dedup + cache ──
async function fetchWithRetry(key, fetcher) {
  // Cache hit
  const cached = _cacheGet(key);
  if (cached !== undefined) return cached;

  // Dedup: if same request is in-flight, piggyback
  if (_inflight.has(key)) return _inflight.get(key);

  const attempt = async () => {
    try {
      const res = await withTimeout(fetcher(), FETCH_TIMEOUT);
      const data = res?.data ?? res;
      _cacheSet(key, data);
      return data;
    } catch (err) {
      // Silent retry once
      try {
        const res = await withTimeout(fetcher(), FETCH_TIMEOUT);
        const data = res?.data ?? res;
        _cacheSet(key, data);
        return data;
      } catch {
        _log('warn', `fetch_failed:${key}`, err?.message);
        return null;
      }
    }
  };

  const p = attempt().finally(() => _inflight.delete(key));
  _inflight.set(key, p);
  return p;
}

// ── Non-blocking logger ──
function _log(level, event, detail) {
  if (process.env.NODE_ENV === 'development') {
    queueMicrotask(() => {
      // eslint-disable-next-line no-console
      console[level === 'warn' ? 'warn' : 'debug'](`[EcoV2] ${event}`, detail || '');
    });
  }
}

// ── Slow network detection ──
function _isSlowNetwork() {
  const conn = navigator?.connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  const type = conn.effectiveType;
  return type === 'slow-2g' || type === '2g';
}

/**
 * Main hook — fetches wallet, flags, and optional extras in parallel.
 */
export function useEconomyV2({ fetchWallet = true, fetchBoost = false, fetchSeason = false, fetchPrompts = false } = {}) {
  const [wallet, setWallet] = useState(null);
  const [boost, setBoost] = useState(null);
  const [season, setSeason] = useState(null);
  const [prompts, setPrompts] = useState(null);
  const [ready, setReady] = useState(false);
  const [slow, setSlow] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    const isSlow = _isSlowNetwork();
    setSlow(isSlow);

    const tasks = [];

    if (fetchWallet) {
      tasks.push(
        fetchWithRetry('wallet', () => api.economyV2Wallet()).then(d => {
          if (mountedRef.current && d) setWallet(d);
        })
      );
    }
    if (fetchBoost && !isSlow) {
      tasks.push(
        fetchWithRetry('boost', () => api.economyV2BoostActive()).then(d => {
          if (mountedRef.current && d) setBoost(d);
        })
      );
    }
    if (fetchSeason && !isSlow) {
      tasks.push(
        fetchWithRetry('season', () => api.economyV2SeasonStatus()).then(d => {
          if (mountedRef.current && d) setSeason(d);
        })
      );
    }
    if (fetchPrompts && !isSlow) {
      tasks.push(
        fetchWithRetry('prompts', () => api.economyV2MonetisationPrompts()).then(d => {
          if (mountedRef.current && d) setPrompts(d);
        })
      );
    }

    // Run all in parallel (max 3 concurrent via browser limit)
    await Promise.allSettled(tasks);
    if (mountedRef.current) setReady(true);
  }, [fetchWallet, fetchBoost, fetchSeason, fetchPrompts]);

  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(() => {
    // Bust cache and reload
    _cache.clear();
    setReady(false);
    load();
  }, [load]);

  return useMemo(() => ({
    wallet, boost, season, prompts, ready, slow, refresh,
  }), [wallet, boost, season, prompts, ready, slow, refresh]);
}

/**
 * Invalidate all cached Economy V2 data (call after mutations).
 */
export function invalidateEconomyV2Cache() {
  _cache.clear();
}
